/* Verbatim port of `const WS = {...}` (service/review/index.html
 * ~3694-4563) — the AM workspace shared-ledger layer (branch am-workspace).
 * The server is the single derivation source for the action board
 * (GET /clients/{slug}/tasks); this layer renders it, drives the task
 * state machine, scoped rechecks, notes, and the what-changed strip.
 * Offline: the derived table renders read-only with a banner — no
 * browser-local writes, no split-brain.
 *
 * ARCHITECTURE (F2d brief step 1+2): the original is one big object mixing
 * data (available/tasks/transitions/diff/notes/author identity/api/init)
 * with rendering (renderBoard/rowHtml/renderStrip/renderNotesCard/dialogs/
 * the task drawer). This file owns the DATA half and orchestration
 * (author identity via lib/identity.ts, api() via api/client.ts's
 * request(), init/migrateOnce/refresh/repaintAll, task-list computations,
 * PATCH transitions with expected_status optimistic concurrency, recheck,
 * notes CRUD, the `?wsf=` deep-link filter). Rendering is split into
 * hub/src/ws/{board,strip,notes,taskDrawer,dialogs}.ts as plain functions
 * taking the Workspace instance as an explicit first parameter (F1/F2
 * porting rule "a" — no closures over `this`); the Workspace class below
 * re-exposes each as an instance method (`renderBoard(el){
 * renderBoardImpl(this, el); }`) so the EXTERNAL surface — every
 * `WS.xxx(...)` call site across views/exec.ts, views/conflicts.ts,
 * ui/exports.ts, and this object's own inline onclick markup — is
 * byte-identical to the original's method list. Nothing outside this file
 * and hub/src/ws/*.ts needs to know the split exists.
 *
 * GLOBAL EXPOSURE: the original `WS` was a real top-level `const`, so
 * every already-ported view (views/exec.ts, views/conflicts.ts) and
 * ui/exports.ts references the bare identifier `WS` WITHOUT importing it
 * — by design (F2a/F2b/F2c brief: "no other edits" once WS lands). Those
 * files type-checked against an ambient `declare const WS: any` shim
 * (hub/src/types/legacy-globals.d.ts, deleted by this task). This module
 * replaces that shim with the real thing: `WS` is assigned onto
 * `globalThis` (so the bare runtime reference still resolves, exactly like
 * an old-style global `<script>` var did) AND declared via `declare
 * global { const WS: Workspace }` (so the same bare reference still
 * type-checks, now against the real class instead of `any`). Every
 * pre-existing `WS.foo` call site keeps compiling and running with ZERO
 * edits.
 *
 * F4 item 3 (store decoupling): `repaintAll()` and `init()`'s post-fetch
 * tail used to hardcode "which renderers care that WS data changed" as a
 * literal call list (renderActions/renderExec/renderConflicts/renderStrip
 * [/renderNotesCard, init() only]). That list is gone — both now call
 * `notifyWsChange()` (state/wsChange.ts), and every renderer that used to
 * be named in it subscribes to `wsChangeStore` itself, at its own module
 * scope: views/actions.ts, views/exec.ts, views/conflicts.ts, ws/strip.ts,
 * ws/notes.ts. This file no longer needs to import renderExec/
 * renderConflicts/ws/strip.ts's renderStrip at all — see each subscriber
 * file's own doc comment for why IT reacts, and ws/board.ts's doc comment
 * for why board.ts deliberately does NOT get an independent subscription
 * (it's reached transitively through views/actions.ts's, which already
 * owns the `#actionsBody` availability branch this pre-dates). Two
 * single-target repaints stay direct, unconverted calls to renderActions
 * — they were never part of the fan-out this decouples: init()'s
 * pre-fetch skeleton paint (nothing else needs to react to "still
 * probing") and switchAuthor()'s toolbar-only repaint.
 */
import { request } from '../api/client.ts';
import { AUTH, type AuthUser } from '../lib/auth.ts';
import { slugify } from '../lib/format.ts';
import { actStatusStore, registerWsAvailability } from './ui.ts';
import { payloadStore } from './payload.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import { notifyWsChange } from './wsChange.ts';
import { openDrawer } from '../ui/drawer.ts';
import { renderActions } from '../views/actions.ts';
import {
  renderBoard as renderBoardImpl,
  renderSkeleton as renderSkeletonImpl,
  rowHtml as rowHtmlImpl,
  statusMenu as statusMenuImpl,
  toggleDensity as toggleDensityImpl,
  filterProg as filterProgImpl,
  buildFilterQuery,
} from '../ws/board.ts';
import { renderKanban as renderKanbanImpl } from '../ws/kanban.ts';
import { svg, type TemplateResult } from 'lit';
import {
  addCustomTask as addCustomTaskImpl,
  ask as askImpl,
  exportCompleted as exportCompletedImpl,
  toast as toastImpl,
} from '../ws/dialogs.ts';
import { renderNotesCard as renderNotesCardImpl } from '../ws/notes.ts';
import { renderDrawerExtras as renderDrawerExtrasImpl, toggleExpand as toggleExpandImpl } from '../ws/taskDrawer.ts';
// Side-effect-only: this module no longer calls ws/strip.ts's renderStrip
// directly (F4 item 3 — it subscribes to wsChangeStore itself instead, see
// its own doc comment), so nothing else in the import graph reaches it.
// Without this import ws/strip.ts's module body — and therefore its
// subscription — would never run, and #wsStrip would silently stay empty
// forever (caught by the parity harness: missing content shifted every
// section below it, ~3-10% mismatch depending on target).
import '../ws/strip.ts';
import type { WsAskOptions, WsBoardFilter, WsBoardOrderRow, WsChecklistItem, WsDiff, WsLabel, WsNote, WsProbe, WsRecheckResponse, WsSavedFilter, WsSavedFilterQuery, WsTask, WsToastAction, WsUrlFilter } from '../types/workspace.ts';
import type { NormalizedAsset } from '../types/contract.ts';

export class Workspace {
  available: boolean | null = null;   // null = probing, true = server board, false = offline
  clientSlug: string | null = null;
  tasks: WsTask[] = [];
  // FIX-audit F8: monotonic init generation. Every init() bumps it and captures
  // its own generation; a commit is discarded if a newer init() has since
  // started (a client switch), so a late-resolving init(A) can never assign A's
  // board/diff/notes under client B.
  private _initGen = 0;
  transitions: Record<string, string[]> = {};
  diff: WsDiff | null = null;
  notes: WsNote[] = [];
  // server-backed user roster (Phase 0 auth, Task 5) — fetched once in
  // init() (GET /users), reused by the assign picker's roster() and by
  // board.ts's actClick 'assign' handler to resolve a picked name to an id.
  users: AuthUser[] = [];
  // Task-Management Phase 2 / L: the global label vocabulary, fetched once in
  // init() (GET /labels) like `users`. Reused by the filter bar's label select
  // and the task-drawer label editor. A transient /labels failure leaves this
  // [] (degrade, don't break) — same treatment as the roster fetch.
  labels: WsLabel[] = [];
  // Task-Management Phase 2 / S: the session user's saved board filters, fetched
  // once in init() (GET /me/filters). Client-agnostic (a saved filter applies on
  // any client's board). A transient failure leaves this [] (degrade, don't
  // break).
  savedFilters: WsSavedFilter[] = [];
  // kanban-parity: the session user's manual within-lane card order for THIS
  // client's board (GET /me/board-order), re-fetched per client switch like the
  // roster/labels. PURE presentation state — applied on top of the derived sort
  // in the kanban view, empty until the AM's first manual drag. A transient
  // failure leaves this [] (degrade to the derived sort, don't break).
  boardOrder: WsBoardOrderRow[] = [];

  _compact = false;
  _progOnly = false;
  _mineOnly = false;
  _notesAll = false;
  // Task-Management Phase 2 / K: List | Board view preference. Persisted
  // per-user in localStorage (no server round-trip — a view preference, not
  // shared task state; same client-side-preference convention as density /
  // identity). Defaults to 'list' (the familiar exception-first grouped board —
  // K4); loadView() reads the persisted value in init() once AUTH is known.
  _view: 'list' | 'board' = 'list';
  // Task-Management Phase 1 / T5: the server-side filter bar's state. Empty
  // strings = inactive; any non-empty field switches the board fetch onto the
  // server's search_tasks path (GET /clients/{slug}/tasks?…). Distinct from the
  // two client-side checkboxes (_progOnly/_mineOnly, still applied on top via
  // filterProg) and from _urlFilter (portfolio deep-links) — those never round-
  // trip. `q` is full-text (title/detail/notes); the rest are exact/bucketed.
  _filter: WsBoardFilter = { q: '', status: '', assignee: '', priority: '', due: '', label: '' };
  _filterTimer: ReturnType<typeof setTimeout> | null = null;
  // Task-Management Phase 1 / T6: the board multi-select set (task ids). A
  // non-empty set surfaces the bulk-action bar (ws/board.ts). Bulk changes go
  // through POST /tasks/bulk, which loops the SAME guarded store fns the
  // single-task PATCH uses — so the evidence-gate, stall-clock and per-id
  // legality all still apply, and illegal ids come back reported (D9), never
  // silently forced. Selection is view-only client state; it clears on a
  // client switch (init) and after a bulk apply.
  _selected: Set<number> = new Set();
  // {kind:'overdue'|'stalled'|'assignee', value?, label} — set by portfolio deep-links
  _urlFilter: WsUrlFilter | null = null;
  _menu: HTMLElement | null = null;
  _menuOwner: HTMLElement | null = null;
  _menuOut: ((ev: MouseEvent) => void) | null = null;

  // short labels on the pill (the widest, most variable element per row —
  // color already does most of the work); full phrasing lives in the
  // tooltip and the transitions menu (design-audit N1)
  readonly STATUS_LABEL: Record<string, string> = {
    'open': 'Open', 'in-progress': 'In progress',
    'waiting-client': 'Waiting', 'waiting-platform': 'Waiting',
    'implemented': 'Implemented', 'verified': 'Verified',
    'regressed': 'Regressed', 'wont-do': "Won't-do",
    // Q-03: a DISTINCT label — never "Verified". The pill is green-family but
    // outlined + person-glyphed (workspace.css .s-attested) so it never reads
    // as the probe-verified pill.
    'attested': 'Attested',
  };
  readonly STATUS_FULL: Record<string, string> = {
    'open': 'Open', 'in-progress': 'In progress',
    'waiting-client': 'Waiting on client', 'waiting-platform': 'Waiting on platform',
    'implemented': 'Implemented — pending verification',
    'verified': 'Verified — confirmed by evidence',
    'regressed': 'Regressed — a verified fix broke again',
    'wont-do': "Won't-do — closed with a reason",
    'attested': 'Attested — human-verified (not a probe); machine evidence still overrides',
  };

  // one stroke-icon family, replacing the emoji glyphs across rail/toolbar/
  // row-meta/receipts (design-audit N4)
  //
  // F3c: converted from raw SVG-markup strings to real svg`` TemplateResults
  // (lit exports the `svg` tag alongside `html`). Each entry's markup is
  // otherwise byte-identical to the original string — lit's svg`` wraps the
  // template in an outer <svg>...</svg> for parsing, then re-parents that
  // wrapper's children (here: the ONE inner <svg class="mi" ...> element)
  // onto the template root, so the produced DOM node is the same
  // `<svg class="mi" viewBox="...">` element the original innerHTML
  // assignment used to produce — verified end-to-end (parity + manual
  // browser check, see the F3c report).
  readonly ICON: Record<string, TemplateResult> = {
    portfolio: svg`<svg class="mi" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="6" rx="1.5"/><rect x="3" y="14" width="18" height="6" rx="1.5"/></svg>`,
    plus: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>`,
    doc: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>`,
    docLines: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h6"/></svg>`,
    down: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M12 3v12M8 11l4 4 4-4"/><path d="M5 21h14"/></svg>`,
    check: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M9 11l3 3 8-8"/><path d="M21 12v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>`,
    user: svg`<svg class="mi" viewBox="0 0 24 24"><circle cx="12" cy="8" r="3.4"/><path d="M5 20a7 7 0 0 1 14 0"/></svg>`,
    cal: svg`<svg class="mi" viewBox="0 0 24 24"><rect x="3.5" y="5" width="17" height="16" rx="2"/><path d="M3.5 9.5h17M8 3v4M16 3v4"/></svg>`,
    star: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"/></svg>`,
    starFill: svg`<svg class="mi f" viewBox="0 0 24 24" stroke="none"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"/></svg>`,
    recheck: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M20 11a8 8 0 1 0-.9 4.5M20 6v5h-5"/></svg>`,
    speech: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
    clock: svg`<svg class="mi" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>`,
    density: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg>`,
    // T4 watch toggle: `eye` = watching, `eyeOff` = not watching. Same stroke
    // family (`class="mi"`, 24×24) as every icon above.
    eye: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>`,
    eyeOff: svg`<svg class="mi" viewBox="0 0 24 24"><path d="M3 3l18 18M10.6 5.1A11 11 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 3.9M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a11 11 0 0 0 4-.7"/></svg>`,
    // P0-3 fix 3: the kanban card's compact status affordance (⋮) — filled
    // dots (`mi f`, like starFill) so they read as dots, not stroked rings.
    kebab: svg`<svg class="mi f" viewBox="0 0 24 24" stroke="none"><circle cx="12" cy="5" r="1.9"/><circle cx="12" cy="12" r="1.9"/><circle cx="12" cy="19" r="1.9"/></svg>`,
  };

  /* one-line method guidance per non-probeable category (probeable families
     get the recheck button instead); from the firm's playbook vocabulary */
  readonly METHOD_HINTS: Record<string, string> = {
    'AI narrative': 'Publish authoritative owned content on the cited themes (IR, newsroom); claim/respond on review platforms; verified by the next run’s AIQ pass — no instant probe exists for AI narratives.',
    'Search defense': 'Elevate owned + Desirable results (content, cross-links, freshness); Wikipedia only via COI-safe Talk-page requests. Verified against IMPACT on the next run — check the SERP history link for movement.',
    'Consistency': 'Fix the facts at the source the wrong value came from (site, Wikidata, profile), then align every surface listed in the receipts. Verified when the next run’s consistency audit stops flagging it.',
    'Custom': 'AM-defined task — verified by the next research run or mark Implemented and attach a page-live recheck on the relevant URL.',
  };

  /* Phase 0 auth (Task 5): identity is now the signed-in Google Workspace
     session (lib/auth.ts's AUTH.user) — set once by main.ts's boot() gate
     (AUTH.init()) before anything in this class ever runs, so `author()`
     never has to prompt or fall back to a browser-local name. */
  author(): string { return AUTH.user?.name || ''; }

  /* NO LONGER prompts (that was the pre-auth "confirm a cached name" flow —
     see the removed round-3 note; a session is now server-verified, so a
     wrong identity can't silently carry over from a shared machine the way
     a localStorage name could). Kept as a method (not inlined at call
     sites) purely so every existing `await ws.ensureAuthor()` call site
     across ws/*.ts — none of which send the returned name to the server
     anymore either, see the step-5 author-strip sites — keeps compiling
     unchanged. */
  async ensureAuthor(): Promise<string> {
    return this.author() || 'AM';
  }

  async switchAuthor(): Promise<void> {
    await AUTH.signOut();
  }

  /* names already active on this client's board — assign picker roster.
     Phase 0 auth: server-backed (this.users, fetched in init()) rather than
     scraped from task/note history, so someone who hasn't touched THIS
     client's board yet still shows up as an assign target. */
  roster(): string[] {
    return this.users.map((u) => u.name).sort();
  }

  /* C7 (phantom-assignee fix): resolve a typed/picked name to a REGISTERED user
     (case-insensitive exact match), or undefined. Assignment must resolve to a
     real user id — an unmatched name must NEVER silently become a phantom owner
     (that breaks My Work / notifications / watchers, which key on real ids). */
  resolveUser(name: string): AuthUser | undefined {
    const n = (name || '').trim().toLowerCase();
    if (!n) return undefined;
    return (this.users || []).find((u) => (u.name || '').toLowerCase() === n);
  }

  async api<T = unknown>(path: string, opts?: RequestInit): Promise<T> {
    return request<T>(path, opts);
  }

  async init(): Promise<void> {
    const gen = ++this._initGen; // FIX-audit F8: this init's generation
    const state = payloadStore.get();
    this.loadView(); // K: restore the per-user List|Board preference (AUTH is set by boot())
    this.clientSlug = slugify(state.brand || '');
    const slug = this.clientSlug; // capture — every fetch below targets THIS client
    this.available = null; this.diff = null; this.notes = []; this.tasks = [];
    // T5: a fresh client starts with no server filter (the bar clears when you
    // switch clients — a stale "status:in-progress" carried across would show a
    // misleading empty board on the new client).
    this._filter = { q: '', status: '', assignee: '', priority: '', due: '', label: '' };
    this._selected.clear(); // T6: never carry a selection across clients
    // portfolio deep-links: ?wsf=overdue | stalled | assignee:Name
    if (this._urlFilter === null) {
      const wsf = new URLSearchParams(location.search).get('wsf');
      if (wsf) {
        const [kind, value] = wsf.split(':');
        if (kind && ['overdue', 'stalled', 'assignee'].includes(kind))
          this._urlFilter = { kind: kind as WsUrlFilter['kind'], value,
            label: kind === 'assignee' ? 'tasks w/ ' + value : kind + ' only' };
      }
    }
    // paint the skeleton immediately — otherwise the previous client's
    // board (or nothing, on first load) sits on screen until the fetch
    // resolves (design-audit X4)
    renderActions(payloadStore.get());
    // FIX-audit F8: `stale()` is true once a newer init() has superseded this
    // one. Every commit below is gated on it (and every URL targets the CAPTURED
    // `slug`), so a late-resolving init for the previous client can never write
    // its board/diff/notes/order under the current client.
    const stale = (): boolean => gen !== this._initGen;
    try {
      const board = await this.api<{ tasks?: WsTask[]; transitions?: Record<string, string[]> }>(`/clients/${slug}/tasks`);
      if (stale()) return; // superseded by a client switch during the fetch — discard
      this.available = true;
      this.tasks = board.tasks || [];
      this.transitions = board.transitions || {};
      await this.migrateOnce();
      if (stale()) return;
      try { const d = await this.api<WsDiff>(`/clients/${slug}/diff`); if (stale()) return; this.diff = d; } catch (e) {}
      try { const n = ((await this.api<{ notes?: WsNote[] }>(`/clients/${slug}/notes`)).notes) || []; if (stale()) return; this.notes = n; } catch (e) {}
      // server-backed roster (Phase 0 auth, Task 5) — guarded independently
      // of the board/diff/notes fetches above so a transient /users failure
      // never sets `available = false` (roster() just returns [] and the
      // assign picker's datalist is empty, same "degrade, don't break"
      // treatment every other try/catch in this method already uses).
      try { const u = ((await this.api<{ users?: AuthUser[] }>('/users')).users) || []; if (stale()) return; this.users = u; } catch (e) {}
      // P2/L: the global label vocabulary — guarded like the roster so a
      // transient /labels failure never sets available=false.
      try { const l = ((await this.api<{ labels?: WsLabel[] }>('/labels')).labels) || []; if (stale()) return; this.labels = l; } catch (e) {}
      // P2/S: the session user's saved board filters (client-agnostic — fetched
      // once per client switch, guarded like the roster/labels).
      try { const f = ((await this.api<{ filters?: WsSavedFilter[] }>('/me/filters')).filters) || []; if (stale()) return; this.savedFilters = f; } catch (e) {}
      // kanban-parity: the session user's manual board order for THIS client
      // (per-client — a saved order is client+lane scoped). Guarded like the
      // roster/labels so a transient failure degrades to the derived sort.
      try { const b = ((await this.api<{ positions?: WsBoardOrderRow[] }>(`/me/board-order?client=${encodeURIComponent(slug)}&view=board`)).positions) || []; if (stale()) return; this.boardOrder = b; } catch (e) { if (!stale()) this.boardOrder = []; }
    } catch (e) {
      if (stale()) return;
      this.available = false;
    }
    if (stale()) return;
    // F4 item 3 (store decoupling): this used to be a hardcoded
    // renderActions/renderExec/renderConflicts/renderStrip/renderNotesCard
    // fan-out (same list `repaintAll()` below used, minus renderNotesCard —
    // an inconsistency this notify() now papers over harmlessly: a
    // subscriber re-rendering with unchanged data is a no-op paint, not a
    // behavior change). Every one of those renderers now subscribes to
    // wsChangeStore itself (state/wsChange.ts) instead of being called out
    // by name here.
    notifyWsChange();
  }

  renderSkeleton(el: HTMLElement): void { renderSkeletonImpl(el); }

  /* one-time import of the old browser-local statuses into the ledger */
  async migrateOnce(): Promise<void> {
    const flag = 'fb-ws-migrated-' + this.clientSlug;
    if (localStorage.getItem(flag)) return;
    const local = actStatusStore.get() || {};
    const meaningful = Object.entries(local).filter(([, v]) => v && v !== 'open');
    if (meaningful.length) {
      try {
        // `author` STRIPPED (Phase 0 auth, Task 5): the server now stamps
        // authorship from the session (require_user, service/auth.py) and
        // 422s on an unrecognized `author` field (Pydantic `extra=forbid`).
        const r = await this.api<{ migrated?: boolean }>(`/clients/${this.clientSlug}/tasks/migrate-local`, {
          method: 'POST',
          body: JSON.stringify({ statuses: Object.fromEntries(meaningful) }),
        });
        if (r.migrated) {
          const b = await this.api<{ tasks?: WsTask[] }>(`/clients/${this.clientSlug}/tasks`);
          this.tasks = b.tasks || [];
        }
      } catch (e) {}
    }
    localStorage.setItem(flag, '1');
  }

  /* T5: the board fetch URL — carries the active server filter as query params
     (empty filter => the bare path, i.e. the full list, byte-identical to the
     pre-T5 fetch). Centralized so init()/refresh()/applyFilter() all agree. */
  boardUrl(): string {
    const qs = buildFilterQuery(this._filter);
    return `/clients/${this.clientSlug}/tasks${qs ? '?' + qs : ''}`;
  }

  hasServerFilter(): boolean { return buildFilterQuery(this._filter).length > 0; }

  async refresh(): Promise<void> {
    try {
      const b = await this.api<{ tasks?: WsTask[]; transitions?: Record<string, string[]> }>(this.boardUrl());
      this.tasks = b.tasks || []; this.transitions = b.transitions || this.transitions;
    } catch (e) {}
    this.repaintAll();
  }

  /* T5: patch the filter state and re-fetch the board through the server. A
     text-box keystroke debounces (250ms) so we don't fire a request per
     character; selects/clears apply immediately (immediate=true). */
  applyFilter(patch: Partial<WsBoardFilter>, immediate = true): void {
    this._filter = { ...this._filter, ...patch };
    if (this._filterTimer) { clearTimeout(this._filterTimer); this._filterTimer = null; }
    const run = () => { this._filterTimer = null; this.refresh(); };
    if (immediate) run();
    else this._filterTimer = setTimeout(run, 250);
  }

  clearFilter(): void {
    this.applyFilter({ q: '', status: '', assignee: '', priority: '', due: '', label: '' });
  }

  /* ── P2/S saved filters (rendering half is ws/savedFilters.ts) ──
     The saved payload is the server board filter (_filter) + the two client-side
     checkboxes (_mineOnly/_progOnly) — pure UI state, round-tripped opaque by the
     server. Applying one restores that state and re-runs the EXISTING
     applyFilter/search_tasks path (coverage/total-honesty unchanged). `author` is
     never sent (the owner is the session user). */
  currentFilterQuery(): WsSavedFilterQuery {
    return { ...this._filter, mineOnly: this._mineOnly, progOnly: this._progOnly };
  }

  applySavedFilter(q: WsSavedFilterQuery): void {
    this._filter = {
      q: q.q || '', status: q.status || '', assignee: q.assignee || '',
      priority: q.priority || '', due: (q.due as WsBoardFilter['due']) || '', label: q.label || '',
    };
    this._mineOnly = !!q.mineOnly;
    this._progOnly = !!q.progOnly;
    // refresh the board through the server + repaint (the toolbar checkboxes and
    // filter selects reflect the restored state on the repaint).
    this.applyFilter({});
  }

  async saveCurrentFilter(name: string): Promise<void> {
    try {
      const r = await this.api<{ filter: WsSavedFilter }>('/me/filters', {
        method: 'POST', body: JSON.stringify({ name, query: this.currentFilterQuery() }),
      });
      const i = this.savedFilters.findIndex((f) => f.id === r.filter.id);
      if (i >= 0) this.savedFilters[i] = r.filter; else this.savedFilters.push(r.filter);
      this.savedFilters.sort((a, b) => a.name.localeCompare(b.name));
      this.toast(`Saved filter “${name}”.`, 'ok');
    } catch (e) { this.toast('Could not save filter: ' + (e as Error).message, 'warn'); }
  }

  async deleteSavedFilter(id: number): Promise<void> {
    try {
      await this.api(`/me/filters/${id}/delete`, { method: 'POST' });
      this.savedFilters = this.savedFilters.filter((f) => f.id !== id);
      this.toast('Saved filter deleted.', 'ok');
    } catch (e) { this.toast('Could not delete filter: ' + (e as Error).message, 'warn'); }
  }

  /* ── kanban-parity: per-view manual within-lane card ordering ──
     PURE presentation state — never touches a task row or the stall clock.
     `orderLaneCards` applies the stored order (if any) ON TOP of the derived
     sort: positioned cards first in their stored order, any card without a
     stored position appended in derived order (a newly-appeared card slots to
     the tail, never hidden). A lane with NO stored rows returns the derived
     order unchanged — the derived sort is the DEFAULT until the first manual
     drag writes a position for that lane. */
  orderLaneCards(laneKey: string, cards: WsTask[], derived: (a: WsTask, b: WsTask) => number): WsTask[] {
    const pos = new Map<number, number>();
    for (const r of this.boardOrder) if (r.lane_key === laneKey) pos.set(r.task_id, r.position);
    if (!pos.size) return [...cards].sort(derived); // derived sort is the default
    const positioned = cards.filter((t) => pos.has(t.id)).sort((a, b) => pos.get(a.id)! - pos.get(b.id)!);
    const rest = cards.filter((t) => !pos.has(t.id)).sort(derived);
    return [...positioned, ...rest];
  }

  laneHasManualOrder(laneKey: string): boolean {
    return this.boardOrder.some((r) => r.lane_key === laneKey);
  }

  /* Persist a lane's manual order after a within-lane reorder drag (POST
     /me/board-order). Optimistically updates this.boardOrder so the repaint
     reflects it without a round-trip; a failed write rolls back. `author` never
     sent (session identity). */
  async saveLaneOrder(laneKey: string, taskIds: number[]): Promise<void> {
    const prev = this.boardOrder;
    this.boardOrder = [
      ...this.boardOrder.filter((r) => r.lane_key !== laneKey),
      ...taskIds.map((id, i) => ({ lane_key: laneKey, task_id: id, position: i })),
    ];
    this.repaintAll();
    try {
      const r = await this.api<{ positions: Array<{ task_id: number; position: number }> }>('/me/board-order', {
        method: 'POST',
        body: JSON.stringify({ client_slug: this.clientSlug, view: 'board', lane_key: laneKey, task_ids: taskIds }),
      });
      this.boardOrder = [
        ...this.boardOrder.filter((x) => x.lane_key !== laneKey),
        ...r.positions.map((p) => ({ lane_key: laneKey, task_id: p.task_id, position: p.position })),
      ];
    } catch (e) {
      this.boardOrder = prev;
      this.repaintAll();
      this.toast('Could not save the card order — reverted.', 'warn');
    }
  }

  /* kanban-parity: create a custom task straight from a lane foot (quick-add).
     Title-only create through the SAME guarded POST as the full modal; if the
     lane's status isn't `open`, a follow-up guarded PATCH moves it there (a
     legal open→waiting/implemented transition — never an evidence lane). The
     board refreshes so the new card lands in its lane. `author` never sent. */
  async quickAddTask(title: string, status: string): Promise<number | null> {
    const t = title.trim();
    if (!t) return null;
    try {
      const r = await this.api<{ task: WsTask }>(`/clients/${this.clientSlug}/tasks`, {
        method: 'POST', body: JSON.stringify({ title: t }),
      });
      if (status && status !== 'open') {
        try {
          await this.api(`/tasks/${r.task.id}`, { method: 'PATCH',
            body: JSON.stringify({ status, expected_status: 'open' }) });
        } catch (e) { /* the card still exists as open — non-fatal */ }
      }
      await this.refresh();
      return r.task.id;
    } catch (e) { this.toast('Could not add card: ' + (e as Error).message, 'warn'); return null; }
  }

  /* ── P2/L label data ops (rendering half is ws/labels.ts) ──
     createLabel: create-or-dedup (server returns the existing row on a name
     conflict), adding a genuinely new one to the in-memory roster so the filter
     select + editor see it without a refetch. toggleTaskLabel: attach/detach,
     mutating the task's labels from the server's returned list (optimistic-safe,
     like toggleWatch — no full refresh() so an open expander survives). Neither
     touches status/reconcile; `author` is never sent (session authorship). */
  async createLabel(name: string, color?: string): Promise<WsLabel | null> {
    try {
      const r = await this.api<{ label: WsLabel }>('/labels', {
        method: 'POST', body: JSON.stringify(color ? { name, color } : { name }),
      });
      if (r.label && !this.labels.some((l) => l.id === r.label.id)) this.labels.push(r.label);
      return r.label;
    } catch (e) { this.toast('Could not create label: ' + (e as Error).message, 'warn'); return null; }
  }

  async toggleTaskLabel(t: WsTask, labelId: number, active: boolean): Promise<void> {
    try {
      const r = await this.api<{ labels: WsLabel[] }>(`/tasks/${t.id}/labels`, {
        method: 'POST', body: JSON.stringify({ label_id: labelId, active }),
      });
      t.labels = r.labels;
    } catch (e) { this.toast('Label update failed: ' + (e as Error).message, 'warn'); }
  }

  /* ── P2/C checklist data ops (rendering half is ws/checklist.ts) ──
     The drawer editor re-fetches the item list on each change (collapse+expand
     reload), so these just POST/PATCH and surface errors; none touch the parent
     task's status (C1 — that's a server invariant too). `author` is never sent
     (session authorship). */
  async fetchChecklist(t: WsTask): Promise<WsChecklistItem[]> {
    try { return ((await this.api<{ items?: WsChecklistItem[] }>(`/tasks/${t.id}/checklist`)).items) || []; }
    catch (e) { return []; }
  }

  async addChecklistItem(t: WsTask, body: string): Promise<void> {
    try {
      await this.api(`/tasks/${t.id}/checklist`, { method: 'POST', body: JSON.stringify({ body }) });
    } catch (e) { this.toast('Could not add item: ' + (e as Error).message, 'warn'); }
  }

  async patchChecklistItem(_t: WsTask, itemId: number, patch: { done?: boolean; body?: string; position?: number }): Promise<void> {
    try {
      await this.api(`/checklist/${itemId}`, { method: 'PATCH', body: JSON.stringify(patch) });
    } catch (e) { this.toast('Checklist update failed: ' + (e as Error).message, 'warn'); }
  }

  async removeChecklistItem(_t: WsTask, itemId: number): Promise<void> {
    try {
      await this.api(`/checklist/${itemId}/remove`, { method: 'POST' });
    } catch (e) { this.toast('Could not remove item: ' + (e as Error).message, 'warn'); }
  }

  // one master list, many surfaces: a status change repaints every place a
  // task appears (board, exec top-5, §6 rows, strip, open drawer) — the
  // render-only half of refresh(), reused by optimistic writes so the paint
  // doesn't wait on a round-trip (design-audit X4).
  //
  // F4 item 3 (store decoupling): the manual renderActions/renderExec/
  // renderConflicts/renderStrip fan-out this used to hardcode is now
  // notifyWsChange() — every one of those renderers subscribes to
  // wsChangeStore itself (see each file's own doc comment). The open-drawer
  // re-paint stays here as an explicit call: it's not one of the modules
  // this task decouples (drawer content is asset-keyed, not ws-keyed), and
  // Store.set()/.update() notify listeners SYNCHRONOUSLY (state/store.ts),
  // so every subscriber above has already repainted by the time this line
  // runs — same ordering the original's trailing openDrawer() call had.
  repaintAll(): void {
    notifyWsChange();
    const state = payloadStore.get();
    if (state.selected) openDrawer(state, state.selected as string);
  }

  isInternal(t: WsTask): boolean {
    // AM-created tasks outside the agreed program are working notes, not
    // client-facing recommendations (round-3: a test drill ranked #3 on the
    // exec top-five and pre-checked itself into the client export)
    return t.action_key.startsWith('custom:') && !t.in_program;
  }

  overdueDays(t: WsTask): number {
    if (!t.due_date || ['verified', 'wont-do', 'attested'].includes(t.status)) return 0;
    const d = Date.parse(t.due_date + 'T23:59:59');
    return d && d < Date.now() ? Math.ceil((Date.now() - d) / 864e5) : 0;
  }

  /* the exec top-five: actionable rows, exceptions first, then priority;
     internal working tasks never rank on the client-facing summary */
  topMoves(n: number): WsTask[] {
    const PR: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const ORD: Record<string, number> = { 'regressed': 0, 'open': 1, 'in-progress': 1, 'implemented': 2,
      'waiting-client': 3, 'waiting-platform': 3 };
    return this.tasks
      .filter((t) => t.status in ORD && !this.isInternal(t))
      .sort((a, b) => (Number(this.overdueDays(b) > 0) - Number(this.overdueDays(a) > 0))
        || ((ORD[a.status] ?? 9) - (ORD[b.status] ?? 9)) // filter above guarantees membership
        || ((PR[a.priority] ?? 2) - (PR[b.priority] ?? 2)))
      .slice(0, n);
  }

  /* ── in-page dialogs: toast + single-field ask (no native prompt/alert —
        they block click-through testing and read as unfinished) ── */
  toast(msg: string, kind?: string, action?: WsToastAction): void { toastImpl(msg, kind, action); }
  ask(opts: WsAskOptions): Promise<string | null> { return askImpl(opts); }

  ageDays(iso?: string | null): number {
    if (!iso) return 0;
    const t = Date.parse(iso); return t ? Math.max(0, Math.floor((Date.now() - t) / 864e5)) : 0;
  }

  /* ── the board (§7) ── */
  renderBoard(el: HTMLElement): void { renderBoardImpl(this, el); }

  /* ── K: kanban view (state/persistence/legality helpers) ── */
  renderKanban(el: HTMLElement): void { renderKanbanImpl(this, el); }

  // per-user localStorage key for the view preference (keyed on the signed-in
  // email so a shared machine doesn't leak one AM's preference to the next).
  private viewKey(): string { return 'fb-ws-view-' + (AUTH.user?.email || 'anon'); }

  loadView(): void {
    try { this._view = localStorage.getItem(this.viewKey()) === 'board' ? 'board' : 'list'; }
    catch (e) { this._view = 'list'; }
  }

  setView(v: 'list' | 'board'): void {
    if (this._view === v) return;
    this._view = v;
    try { localStorage.setItem(this.viewKey(), v); } catch (e) {}
    renderActions(payloadStore.get()); // repaint #actionsBody into the chosen view
  }

  // the legal PATCH targets for a status — the server's PATCH_TRANSITIONS row
  // (carried in this.transitions). verified/regressed are absent from every
  // value, so they are NEVER returned as a drop target (evidence-only).
  legalDropTargets(status: string): string[] { return this.transitions[status] || []; }

  toggleDensity(btn: HTMLElement): void { toggleDensityImpl(this, btn); }

  filterProg(rows: WsTask[]): WsTask[] { return filterProgImpl(this, rows); }

  stateAge(t: WsTask): number { return this.ageDays(t.status_changed_at || t.created_at); }

  rowHtml(t: WsTask, cls: string): TemplateResult { return rowHtmlImpl(this, t, cls); }

  // wireBoard removed F3c — folded into rowHtml's own @click bindings
  // (ws/board.ts's `actClick`); it had zero external callers (grepped),
  // same "remove rather than leave inert" treatment main.ts's window-shim
  // checklist uses for dead entries.

  statusMenu(btn: HTMLElement, t: WsTask): void { statusMenuImpl(this, btn, t); }

  closeMenu(returnFocus?: boolean): void {
    if (this._menu) { this._menu.remove(); this._menu = null; }
    if (this._menuOut) { document.removeEventListener('click', this._menuOut); this._menuOut = null; }
    if (returnFocus && this._menuOwner) this._menuOwner.focus();
    this._menuOwner = null;
  }

  async setField(t: WsTask, field: string, value: unknown): Promise<void> {
    if (value === null || value === undefined) return;
    // `author` STRIPPED (Phase 0 auth, Task 5) — see migrateOnce()'s comment.
    try {
      await this.api(`/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify(
        { [field]: value }) });
    } catch (e) { this.toast('Update failed: ' + (e as Error).message, 'warn'); }
    this.refresh();
  }

  /* ── T4 watch toggle (eye icon in the row expander) ──
     Watch/unwatch the task for the signed-in user. Watchers receive the
     system notification kinds (regression/due/stall). A manual unwatch is
     STICKY server-side (auto-watch can't silently re-subscribe you — D7). The
     new state is mutated back onto the in-memory task so the board keeps it
     across the next repaint; no full refresh() (that would collapse the open
     expander the button lives in). `author` is NOT sent — the watcher is
     always the session user. */
  async toggleWatch(t: WsTask): Promise<void> {
    const active = !t.watching;
    try {
      const r = await this.api<{ watching: boolean; watcherCount: number }>(
        `/tasks/${t.id}/watch`, { method: 'POST', body: JSON.stringify({ active }) });
      t.watching = r.watching;
      t.watcherCount = r.watcherCount;
      this.toast(active ? 'Watching this task — you’ll get its alerts.' : 'Stopped watching this task.', 'ok');
    } catch (e) {
      this.toast('Watch update failed: ' + (e as Error).message, 'warn');
    }
  }

  /* ── T6 board multi-select + bulk apply ── */

  // toggle one row's selection; repaint so the checkbox + bulk bar update.
  toggleSelect(id: number, on: boolean): void {
    if (on) this._selected.add(id); else this._selected.delete(id);
    this.repaintAll();
  }

  clearSelection(): void {
    if (!this._selected.size) return;
    this._selected.clear();
    this.repaintAll();
  }

  // the selected tasks that still exist on the board (prunes stale ids left
  // over from a refresh that dropped a task) — the single source the bulk bar
  // and bulkApply both read, so a gone task never lingers in a request.
  selectedTasks(): WsTask[] {
    return this.tasks.filter((t) => this._selected.has(t.id));
  }

  // select every task currently shown (after the client-side filterProg pass),
  // so "select all" respects the my-tasks / program-only / deep-link filters.
  selectAllShown(): void {
    for (const t of this.filterProg(this.tasks)) this._selected.add(t.id);
    this.repaintAll();
  }

  /* Apply one status transition and/or field change to every selected task via
     POST /tasks/bulk. The server loops the guarded transition/field store fns
     per id (D9 apply-legal-and-report): the legal ids apply, illegal/missing
     ids come back in `failed` — surfaced in the summary toast, never silently
     dropped. `author` is NOT sent (session authorship, extra="forbid"). */
  async bulkApply(change: Record<string, unknown>): Promise<void> {
    const ids = this.selectedTasks().map((t) => t.id);
    if (!ids.length) return;
    try {
      const res = await this.api<{
        appliedCount: number; failedCount: number; failed: Array<{ id: number; error: string }>;
      }>('/tasks/bulk', { method: 'POST', body: JSON.stringify({ ids, ...change }) });
      const ok = res.appliedCount ?? 0;
      const bad = res.failedCount ?? 0;
      if (bad === 0) {
        this.toast(`Updated ${ok} task${ok === 1 ? '' : 's'}.`, 'ok');
      } else {
        const why = (res.failed || []).map((f) => f.error).filter((e, i, a) => a.indexOf(e) === i);
        this.toast(
          `Updated ${ok}; ${bad} couldn't change (${why.slice(0, 2).join('; ')}).`,
          ok ? 'warn' : 'warn',
        );
      }
      this._selected.clear();
    } catch (e) {
      this.toast('Bulk update failed: ' + (e as Error).message, 'warn');
    }
    this.refresh();
  }

  // C7: bulk-assign only to a REGISTERED user (by id). A free-typed name that
  // resolves to nobody is rejected with a clear message — never a phantom owner
  // across a whole selection (the same discipline as the single assign path).
  bulkAssign(name: string): void {
    const u = this.resolveUser(name);
    if (!u) {
      this.toast(`"${name}" isn't on the roster — pick a registered teammate (assignment can't be a free-typed name).`, 'warn');
      return;
    }
    this.bulkApply({ assignee_user_id: u.id });
  }

  /* which probe verifies which task family */
  probeFor(t: WsTask): WsProbe | null {
    const state = payloadStore.get();
    const k = t.action_key;
    if (k === 'wikidata-worksheet')
      return { probe: 'wikidata-claim', hint: 're-pull the Wikidata item and check every worksheet claim' };
    if (k === 'cross-links') {
      // NormalizedAsset narrows normally since the Batch C longhand rewrite
      // (types/contract.ts) — the old `: any` Omit-collapse workaround that
      // used to be documented here is gone with it.
      const core = state.assets.find((a) => a.isCoreEntity);
      if (!core || !core.url) return null;
      const weak = (state.linkGraph?.weakChains || [])
        .map((w) => state.assets.find((a) => a.id === w.assetId)?.url)
        .filter((u): u is string => !!u);
      return { probe: 'page-facts', target: core.url, expect: weak.slice(0, 25),
        hint: 're-crawl the site and check the sameAs/outbound links for every weak-chain profile' };
    }
    if (/^opp:/.test(k)) {
      // prefill from the mapped asset when the run already knows the URL —
      // a hand-typed typo becomes a permanently wrong evidence row
      const platform = (t.title.split('—')[1] || '').trim().toLowerCase();
      const asset = platform ? state.assets.find((a) =>
        (a.platform || '').toLowerCase() === platform
        || (a.title || '').toLowerCase().includes(platform)) : null;
      const what = /^opp:fix/.test(k) ? 'fixed page' : 'claimed/created profile';
      return { probe: 'page-live',
        ask: `URL of the ${what} to verify it is live:`,
        prefill: (asset && asset.url) || '', hint: 'fetch the page and confirm it is live' };
    }
    // W2-F: Wikipedia article-content tasks — the workroom's "PUBLISHED IS NOT
    // VERIFIED" lifecycle reaches Verified through a real deterministic probe.
    // Only for tasks NOT matched above (asset-linked to a Wikipedia article, or a
    // wiki:* content key), so no existing probe mapping changes. The AM enters the
    // exact text that should now be live; the probe fetches the current revision
    // and confirms it — evidence, never a click.
    const wikiArticle = this.wikiArticleForTask(state, t, k);
    if (wikiArticle)
      return { probe: 'article-content', target: wikiArticle, askFills: 'expect',
        ask: 'Exact text that should now be visible in the article (the landed edit):',
        hint: 'fetch the live article revision and confirm the exact text is present' };
    return null;
  }

  /** The canonical Wikipedia article URL an article-content task verifies:
   * the task's own linked Wikipedia asset, else — for a wiki:* content key — the
   * client's primary English article. Null when the task is not about a
   * Wikipedia article (so probeFor falls through to no probe). */
  private wikiArticleForTask(state: AppPayloadState, t: WsTask, k: string): string | null {
    const isArticleUrl = (url: string | null | undefined): boolean => {
      if (!url) return false;
      try {
        const u = new URL(url);
        return /\.wikipedia\.org$/.test(u.hostname.toLowerCase()) && u.pathname.startsWith('/wiki/');
      } catch {
        return false;
      }
    };
    if (t.asset_id) {
      const a = state.assets.find((x) => x.id === t.asset_id);
      if (a && isArticleUrl(a.url)) return a.url;
    }
    if (/^wiki:(coi|unsourced|npov|undue|blp|hostile|content|edit|request)/.test(k)) {
      const articles = state.assets.filter((x) => isArticleUrl(x.url));
      const en = articles.find((x) => {
        try {
          return new URL(x.url as string).hostname.toLowerCase() === 'en.wikipedia.org';
        } catch {
          return false;
        }
      });
      return (en || articles[0])?.url ?? null;
    }
    return null;
  }

  async recheck(t: WsTask, btn: HTMLButtonElement): Promise<void> {
    const p = this.probeFor(t); if (!p) return;
    let target = p.target; let expect = p.expect;
    if (p.ask) {
      const fillsExpect = p.askFills === 'expect';
      const v = await this.ask({ title: 'Recheck', hint: p.ask,
        placeholder: fillsExpect ? 'text now live in the article' : 'https://…',
        value: p.prefill || '', ok: 'Run check' });
      if (!v) return;
      // article-content: the answer is the exact landed text to confirm present.
      if (fillsExpect) expect = { contains: [v] };
      else target = v;
    }
    // `author` STRIPPED (Phase 0 auth, Task 5) — see migrateOnce()'s comment.
    btn.disabled = true; btn.textContent = 'checking…';
    try {
      const r = await this.api<WsRecheckResponse>(`/clients/${this.clientSlug}/recheck`, { method: 'POST',
        body: JSON.stringify({ probe: p.probe, target: target || '', expect,
          task_id: t.id }) });
      if (r.verdict === 'observed')
        this.toast(r.taskVerified ? '✓ Observed — task stamped VERIFIED with evidence.' : '✓ Observed — evidence saved. (Auto-verifies only from "Implemented".)', 'ok');
      else if (r.verdict === 'not-observed')
        this.toast('✗ Not observed — the fix is not visible yet. Evidence saved to the task history.', 'warn');
      else
        this.toast('? Unavailable — ' + ((r.evidence && (r.evidence.reason || 'fetch failed'))) + '. Not counted as a failure.', '');
    } catch (e) { this.toast('Recheck failed: ' + (e as Error).message, 'warn'); }
    this.refresh();
  }

  async toggleExpand(row: HTMLElement, t: WsTask): Promise<void> { return toggleExpandImpl(this, row, t); }

  async addCustomTask(noteBody?: string, noteId?: number): Promise<void> { return addCustomTaskImpl(this, noteBody, noteId); }

  /* ── export completed work for Claude: issue + what was done + evidence.
        Feed the JSON to Claude to draft the client's work-completed report. ── */
  async exportCompleted(): Promise<void> { return exportCompletedImpl(this); }

  // WS.renderStrip() method — REMOVED F4 item 3: its only two callers were
  // init()'s post-fetch tail and repaintAll(), both now notifyWsChange()
  // (ws/strip.ts subscribes to that directly instead). Grepped repo-wide —
  // zero other callers — so this is dead-wiring removal, not a behavior
  // change; ws/strip.ts's own renderStrip(ws, state) export (the actual
  // implementation) is unchanged and still fully reachable via that
  // subscription.

  /* ── notes & ideas card (s0) ── */
  renderNotesCard(): void { renderNotesCardImpl(this); }

  /* ── asset drawer extras ── */
  renderDrawerExtras(a: NormalizedAsset): void { renderDrawerExtrasImpl(this, payloadStore.get(), a); }
}

export const WS = new Workspace();

// Real global exposure — see the module doc comment above for why. Every
// inline onclick string this object's own templates render (ws/board.ts:
// `onclick="WS.addCustomTask()"` etc.) resolves `WS` by looking it up on
// `window`/`globalThis` at click time, exactly like a classic global
// `<script>` var would; every already-ported view keeps resolving the bare
// `WS` identifier the same way at render time.
globalThis.WS = WS;

declare global {
  // `var`, not `const`/`let`: only an ambient `var` declaration adds a
  // property to `globalThis`'s own type (TS models `var` as a real global-
  // object property, matching actual JS semantics) — needed so the plain
  // `globalThis.WS = WS` assignment above type-checks without a cast. This
  // eslint-disable was already here (pre-dating this task) for exactly
  // this `var`; nothing else changed.
  // eslint-disable-next-line no-var
  var WS: Workspace;
}

registerWsAvailability(() => WS.available === true);
